import { Link } from 'react-router-dom';
import { ArrowLeft, Clock, Shield, ListChecks, Sparkles } from 'lucide-react';

export default function BlogPolymarketPlaybook() {
  return (
    <div className="min-h-screen bg-dark-900 text-dark-100">
      <div className="max-w-4xl mx-auto px-4 py-10 md:py-14 space-y-10">
        <header className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-[0.2em] text-primary-400 flex items-center gap-2">
                <span className="inline-flex h-1.5 w-1.5 rounded-full bg-primary-400" />
                Polymarket copy trading
              </p>
              <h1 className="text-2xl md:text-3xl font-bold leading-tight">
                The Ultimate Polymarket Copy Trading Playbook with BetMimic
              </h1>
              <p className="text-sm md:text-base text-dark-300 max-w-2xl">
                This is a practical operating manual for running Polymarket copy trading with BetMimic. From first
                deposit to managing multiple configs with professional-style risk, we will walk through the entire
                lifecycle step-by-step.
              </p>
            </div>
            <div className="hidden sm:flex flex-col items-end gap-2 text-xs text-dark-400">
              <div className="inline-flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>30+ min read</span>
              </div>
              <div className="inline-flex items-center gap-1">
                <Shield className="w-4 h-4 text-emerald-400" />
                <span>Focus: risk-aware automation</span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 text-xs text-dark-400">
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-primary-400 hover:text-primary-300 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to all articles
            </Link>
            <span className="hidden sm:inline">·</span>
            <span>Audience: beginners to grinding Polymarket regulars.</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 text-xs text-dark-300 bg-dark-800/70 border border-dark-700 rounded-2xl p-4 mt-2">
            <div className="space-y-1">
              <p className="font-semibold text-dark-100 flex items-center gap-1.5">
                <ListChecks className="w-4 h-4 text-primary-400" />
                What you will learn
              </p>
              <ul className="list-disc list-inside space-y-1">
                <li>How BetMimic plugs into the official Polymarket CLOB.</li>
                <li>How to prepare your wallet, USDC.e, and allowances.</li>
                <li>How to build, test, and scale copy configs safely.</li>
              </ul>
            </div>
            <div className="space-y-1">
              <p className="font-semibold text-dark-100">Who this is for</p>
              <ul className="list-disc list-inside space-y-1">
                <li>People who believe Polymarket has edge but lack time.</li>
                <li>Traders who want automation without giving up custody.</li>
                <li>Copy trading users who care about real PnL and limits.</li>
              </ul>
            </div>
            <div className="space-y-1">
              <p className="font-semibold text-dark-100">What you need first</p>
              <ul className="list-disc list-inside space-y-1">
                <li>A supported wallet and Polymarket account.</li>
                <li>USDC.e ready to trade on the same chain as Polymarket.</li>
                <li>Access to BetMimic and a few minutes of focus.</li>
              </ul>
            </div>
          </div>
        </header>

        <main className="space-y-10 text-sm md:text-base leading-relaxed text-dark-200">
          <section className="space-y-3">
            <h2 className="text-lg md:text-xl font-semibold">1. How BetMimic actually copies Polymarket trades</h2>
            <p>
              BetMimic is not a random bot guessing prices. Under the hood it talks to the same{' '}
              <span className="font-medium text-primary-300">central limit order book (CLOB)</span> that powers
              Polymarket itself. When a trader you follow places a limit or market order on Polymarket, BetMimic can
              see that activity, normalize it, and submit a corresponding order for your own wallet.
            </p>
            <p>
              That matters for one key reason: your PnL is based on real on-chain trades, not screenshots or rough
              estimates. Positions, fills, and settlements are reconciled against Polymarket&apos;s own data APIs, which
              is why your Dashboard, Positions page, and trade history in BetMimic can line up with what you would see
              if you pulled the raw data yourself.
            </p>
            <p>
              The practical takeaway: when you configure copy trading in BetMimic, you are deciding <span className="font-medium">how</span>
              {' '}to mirror someone else&apos;s orders – not whether the trades exist. The trades are real. Your job is to
              define how much size, how much risk, and under what constraints you are willing to follow them.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg md:text-xl font-semibold">2. The right mindset before you turn anything on</h2>
            <p>
              Before you connect a wallet or configure a single trader, you want to be brutally honest about your
              goals and risk tolerance. Polymarket is sharp. Many markets effectively crowd-source the smartest people
              on the internet. Copy trading does not magically turn you into a genius – it simply lets you plug into
              other people&apos;s edge while keeping full custody of your funds.
            </p>
            <p>
              A helpful way to think about BetMimic is as a{' '}
              <span className="font-medium">position sizing and execution layer</span>. The engine does not tell you
              who to copy or how aggressive you should be. It gives you the rails: max position sizes, stop losses,
              daily loss limits, and portfolio-level constraints. You choose the parameters.
            </p>
            <p>
              A few mindset rules that consistently separate survivors from blow-ups:
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>Assume long-term variance. Even sharp traders have brutal downswings.</li>
              <li>
                Treat each copy config like a &quot;mini fund&quot; with its own budget rather than letting one whale dominate
                your whole bankroll.
              </li>
              <li>
                Decide in advance when you will pause, reduce size, or stop following a trader – before you are tilted.
              </li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg md:text-xl font-semibold">3. Technical setup: wallet, USDC.e, and allowances</h2>
            <p>
              BetMimic never takes custody of your funds. You keep full control of your Polymarket wallet. To copy
              trades, two things need to be true:
            </p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Your wallet must be connected and authorized to use BetMimic.</li>
              <li>
                Your USDC.e token must be approved (allowance) so that the Polymarket conditional tokens contract can
                actually move funds when orders fill.
              </li>
            </ol>
            <p>
              When you connect your wallet in the app, the backend will try to derive or create an API key with the
              official Polymarket CLOB client. It will then continuously monitor your{' '}
              <span className="font-medium">USDC.e balance and allowances</span>. If approvals are missing or too low,
              copy trades will fail before they are ever submitted.
            </p>
            <p>
              As a best practice, treat allowance setup as a separate onboarding step:
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>
                Decide on a comfortable ceiling for how much USDC.e you want Polymarket to be able to move for this
                wallet (for example, 1–3x your initial bankroll).
              </li>
              <li>
                Approve that amount, then verify in BetMimic that allowance checks are passing before enabling any
                copy configs.
              </li>
              <li>
                Revisit allowances periodically as you scale; do not just set them to infinity without thinking.
              </li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg md:text-xl font-semibold">4. Designing your first copy config</h2>
            <p>
              A copy config in BetMimic defines how you mirror a specific Polymarket trader. You choose <span className="font-medium">who</span>
              {' '}to copy, <span className="font-medium">how large</span> your positions can be, and <span className="font-medium">what risk
              rails</span> the engine should enforce.
            </p>
            <p>
              When you create your first config, start conservative and explicit:
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>
                <span className="font-medium">Per-trade sizing:</span> decide if you want fixed dollar amounts per
                trade, a fraction of your bankroll, or a fraction of the source trader&apos;s size.
              </li>
              <li>
                <span className="font-medium">Max position size:</span> set a hard USD cap per market so a single
                outcome cannot nuke you.
              </li>
              <li>
                <span className="font-medium">Stop-loss and take-profit:</span> use percentage or dollar-based exits
                enforced by the backend monitor.
              </li>
            </ul>
            <p>
              Before you turn the config on in size, consider doing a short &quot;shadow run&quot; with small amounts. Let the
              engine take real trades for a few days at a tiny scale, then compare PnL and execution quality against
              what the source trader did. If slippage and fill rates look reasonable, you can scale up.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg md:text-xl font-semibold">5. Scaling up: bankroll frameworks that don&apos;t implode</h2>
            <p>
              Once you trust the plumbing – wallet connection, allowances, and config behavior – the next challenge is
              scaling without silently taking ruin-level risk. A few simple bankroll frameworks go a very long way:
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>
                <span className="font-medium">Config buckets:</span> treat each copied trader as its own bucket with a
                fixed budget. For example, 20–30% of your total bankroll per high-conviction trader instead of letting
                one account swallow everything.
              </li>
              <li>
                <span className="font-medium">Daily loss limits:</span> cap the maximum allowed drawdown in a rolling
                24-hour window. If that loss is hit, configs pause until you manually review.
              </li>
              <li>
                <span className="font-medium">Payout recycling rules:</span> decide how much of realized profit gets
                recycled into larger size versus pulled out or parked.
              </li>
            </ul>
            <p>
              BetMimic&apos;s job is to enforce the rules you set. That means the rules have to be clear. The more
              ambiguous your plan (&quot;I&apos;ll just watch it and decide&quot;), the harder it is to act rationally during
              volatile markets.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg md:text-xl font-semibold">6. Day-to-day operations: how to actually run this</h2>
            <p>
              With configs live, your day-to-day workflow should look boring in a good way. A typical operating rhythm
              for many users looks like this:
            </p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Check the Dashboard once or twice a day for PnL and open positions.</li>
              <li>Review the Recent Trades list to spot any weird markets or unusual sizing.</li>
              <li>
                Scan your copy configs to see if any traders have gone quiet, changed behavior, or hit a loss threshold
                that warrants review.
              </li>
              <li>
                Once a week, reconcile your realized PnL against Polymarket positions and your own notes to ensure
                everything lines up.
              </li>
            </ol>
            <p>
              If something looks off – missing trades, strange position sizes, or unexpected closes – dig into the
              Positions and Trades pages first. BetMimic keeps a full history of trades and position updates based on
              data pulled from Polymarket, which makes debugging a lot easier than scrolling Discord screenshots.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg md:text-xl font-semibold">7. Common mistakes to actively avoid</h2>
            <p>
              Even with good tooling, it is very easy to sabotage yourself. Here are patterns we see over and over that
              you should consciously avoid:
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>Turning on a new config at full size before you understand the trader&apos;s style.</li>
              <li>
                Ignoring correlations – copying multiple traders who all pile into the same macro narratives as if
                they were independent bets.
              </li>
              <li>
                Letting allowances or balances quietly run low so that only some trades execute, breaking your risk
                assumptions.
              </li>
              <li>
                Chasing &quot;revenge configs&quot; after a bad run instead of pausing and reviewing what actually happened.
              </li>
            </ul>
            <p>
              The solution to most of these is unsexy: smaller size, clearer limits, and a weekly review habit. BetMimic
              gives you the controls. Use them.
            </p>
          </section>

          <section className="space-y-4 bg-dark-800/70 border border-dark-700 rounded-2xl p-4 md:p-5">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-primary-400" />
              <h2 className="text-sm md:text-base font-semibold">8. A simple checklist before you scale</h2>
            </div>
            <p className="text-sm text-dark-300">
              Use this as a quick pre-flight list whenever you add a new trader or increase size:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Wallet connected and verified inside BetMimic.</li>
              <li>USDC.e balance is sized for the maximum risk you plan to take.</li>
              <li>Allowances set to a conscious ceiling, not infinity by default.</li>
              <li>Per-config max position size and stop-loss / take-profit defined.</li>
              <li>Daily or session loss limits in place for the overall account.</li>
              <li>You understand the trader&apos;s general style, tempo, and markets.</li>
              <li>You are comfortable pausing configs if metrics deviate from plan.</li>
            </ul>
            <p className="text-sm text-dark-300">
              If you can honestly check every box, scaling is still risky – but it is at least{' '}
              <span className="font-medium">intentional</span> risk instead of blind guessing. That is the entire point of
              using BetMimic for Polymarket copy trading: real automation, backed by real data, with controls you
              actually understand.
            </p>
          </section>

          <section className="space-y-3 border-t border-dark-800 pt-6">
            <p className="text-sm text-dark-400">
              When you are ready for specific configuration ideas, check out the other guides in the BetMimic blog –
              especially the strategy and risk articles. Together, they form a complete toolkit for turning Polymarket
              copy trading from a vague idea into a structured system.
            </p>
            <div className="flex flex-wrap items-center gap-3 text-xs">
              <Link
                to="/blog/polymarket-copy-trading-strategies"
                className="inline-flex items-center gap-1.5 text-primary-400 hover:text-primary-300"
              >
                Explore concrete strategy setups
              </Link>
              <span className="hidden sm:inline text-dark-600">·</span>
              <Link
                to="/blog/polymarket-risk-management"
                className="inline-flex items-center gap-1.5 text-primary-400 hover:text-primary-300"
              >
                Revisit risk management principles
              </Link>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
